import tenantUtils from '@utils';
import tenantConstants from '@constants';
import { imageStateObject } from '../../../helpers/imageHelpers/imageStateObject';
import { getPlatformsAndProducts, getPositionSuffix } from '../../../utility/utility';
import { parseUserNudges } from '../../../utility/nudgeSuppression';
import { t } from 'i18next';

const defaultPreferences = {
  area_unit: 'marla',
  automated_reports: '0',
  currency: tenantConstants.CURRENCY,
  email_notification: '0',
  newsletter: '0',
  push_notifications: 'disabled',
};

export const profileDataMapper = (values) => {
  if (!values) return null;
  return {
    ...values,
    id: values?.id,
    agencyId: values?.agency?.id,
    fALLicenseNumber: values?.license?.number
      ? values?.license?.number
      : values?.license?.license_owner_type === 'Agency'
        ? values?.agency_attributes?.license_attributes?.number
        : values?.license_attributes?.number,
    fallid: values?.license?.id,
    fALLicenseExpiryDate: values?.license?.end_date,
    commercialRegistrationNumber: values?.commercial_registration_number,
    typeofBuisness: values?.broker_type,
    buisnessName: values?.agency?.name,
    nationalShortAddress: values?.national_short_address,
    is_nafaz_verified: values?.is_nafaz_verified,
    is_rega_verified: values?.is_rega_verified,
    license_type: values?.license?.license_owner_type,
    falLicenseURL: values?.license?.certificate_link
      ? values?.license?.certificate_link
      : values?.license?.license_owner_type === 'Agency'
        ? values?.agency_attributes?.license_attributes?.certificate_link
        : values?.license_attributes?.certificate_link,
    nameArabic: values?.name_l1,
    name: values?.name,
    email: values?.email,
    is_email_verified: values?.is_email_verified,
    mobile: values?.mobile ? tenantUtils.formatMobile(values?.mobile, 'singleNumber') : '',
    is_mobile_verified: values?.is_mobile_verified,
    phone: values?.phone ? tenantUtils.formatMobile(values?.phone, 'singleNumber') : '',
    address: tenantUtils.getLocalisedString(values, 'address'),
    country: values?.country_id || 155,
    location: {
      city: values?.city_id && { location_id: values?.city_id, name: tenantUtils.getLocalisedString(values, 'city') },
    },
    ...(values?.profile_image && {
      profile_image: [{ gallerythumb: values?.profile_image, ...imageStateObject() }],
    }),
    whatsapp: values?.whatsapp ? tenantUtils.formatMobile(values?.whatsapp, 'singleNumber') : '',
  };
};

const userDetailTransformer = async (response) => {
  localStorage.setItem('showBanner', JSON.stringify(true));
  const data = response?.user;
  let settings = {};
  Object.keys(defaultPreferences).forEach(
    (e) => (settings[e] = data?.[e]?.value == '0' ? false : data?.[e]?.value == '1' ? true : data?.[e]?.value),
  );
  const isOnOlx = !!data?.olx_user_id;
  const isMultiPlatform = data?.platform_mapping?.bayut?.mapped && data?.platform_mapping?.dubizzle?.mapped;

  return {
    ...data,
    banners: Array.isArray(response?.banners) ? response.banners : [],
    id: data?.id,
    can_post_limit_listings: true,
    type: 'premium',
    permissions: data?.is_agency_admin ? tenantUtils.getPermissionsObj(true) : tenantUtils.getPermissionsObj(false),
    is_owner: data?.is_agency_admin,
    isMultiPlatform: isMultiPlatform,
    can_post_free: { zameen: false, olx: false },
    listing_posted: !!data?.is_listing_posted,
    platform_user: isOnOlx ? [{ id: 2, slug: 'olx', title: 'OLX' }] : [],
    settings: settings,
    ...getPlatformsAndProducts(data?.platform_mapping),
    isCurrencyUser: data?.is_credit_user,
    is_single_platform: !isOnOlx,
    is_shifted_to_olx_quota: data?.is_quota_shifted,
    olx_user_id: data?.olx_user_id,
    isLoggedinUser: true,
    isLoading: false,
    parsedNudges: parseUserNudges(data?.nudges),
  };
};

const getLoggedInUserValues = (values) => {
  if (!values) return null;
  return {
    ...values,
    id: values?.id,
    agencyId: values?.agency?.id,
    fALLicenseNumber: values?.license?.number
      ? values?.license?.number
      : values?.license?.license_owner_type === 'Agency'
        ? values?.agency_attributes?.license_attributes?.number
        : values?.license_attributes?.number,
    fallid: values?.license?.id,
    fALLicenseExpiryDate: values?.license?.end_date,
    commercialRegistrationNumber: values?.commercial_registration_number,
    typeofBuisness: values?.broker_type,
    buisnessName: values?.agency?.name,
    nationalShortAddress: values?.national_short_address,
    is_nafaz_verified: values?.is_nafaz_verified,
    is_rega_verified: values?.is_rega_verified,
    license_type: values?.license?.license_owner_type,
    falLicenseURL: values?.license?.certificate_link
      ? values?.license?.certificate_link
      : values?.license?.license_owner_type === 'Agency'
        ? values?.agency_attributes?.license_attributes?.certificate_link
        : values?.license_attributes?.certificate_link,
    nameArabic: values?.name_l1,
    name: values?.name,
    email: values?.email,
    is_email_verified: values?.is_email_verified,
    mobile: values?.mobile ? tenantUtils.formatMobile(values?.mobile, 'singleNumber') : '',
    is_mobile_verified: values?.is_mobile_verified,
    phone: values?.phone ? tenantUtils.formatMobile(values?.phone, 'singleNumber') : '',
    address: tenantUtils.getLocalisedString(values, 'address'),
    country: values?.country_id || 155,
    location: {
      city: values?.city_id && { location_id: values?.city_id, name: tenantUtils.getLocalisedString(values, 'city') },
    },
    ...(values?.profile_image && {
      profile_image: [{ gallerythumb: values?.profile_image, ...imageStateObject() }],
    }),
    whatsapp: values?.whatsapp ? tenantUtils.formatMobile(values?.whatsapp, 'singleNumber') : '',
    agentDescription: values?.description,
    agentDescriptionArabic: values?.description_l1,
    serviceArea: values?.service_areas ? values?.service_areas?.map((e) => e?.id) : values?.city_id,
    languages: values?.languages ? values?.languages?.map((e) => e?.id) : null,
    experience: values?.experience?.value,
  };
};

export const settingsDetailDataMapper = (response) => {
  return getLoggedInUserValues({
    ...response?.user,
    languageList: response?.languages,
    experienceList: response?.experience?.map((e) => ({
      id: e?.value,
      name: tenantUtils.getLocalisedString(e, 'label'),
    })),
  });
};

const truBrokerLeaderboardMapper = (response) => {
  if (response) {
    if (response?.error) {
      return response;
    }
    return {
      list: response?.users?.map((e) => {
        return {
          ...e,
          rank_details: e?.rank ? t(e?.rank + getPositionSuffix(e?.rank)) : '-',

          agent_details: {
            name: tenantUtils.getLocalisedString(e, 'name'),
            user_agency_name: tenantUtils.getLocalisedString(e?.agency, 'name'),
            image: e?.profile_image,
          },
          tru_points: e?.score ? e?.score : '-',
        };
      }),

      table: [
        {
          title: 'Position',
          dataIndex: 'rank_details',
          key: 'rank_details',
          component: 'String',
        },

        {
          title: 'Name',
          dataIndex: 'agent_details',
          key: 'agent_details',
          component: 'StaffDetails',
        },
        {
          title: 'TruPoints™',
          dataIndex: 'tru_points',
          key: 'tru_points',
          component: 'String',
        },
      ],
      pagination: response?.pagination || null,
    };
  }
};

export default {
  userDetailTransformer,
  profileDataMapper,
  settingsDetailDataMapper,
  truBrokerLeaderboardMapper,
};
